import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { T } from "../components/T";
import { useMutation, useQuery } from "@apollo/client";
import {
  EDIT_PROJECT,
  PROJECTS,
  GET_PROJECTS_BY_STATUS,
} from "../graphql/gqlProject";
import { useParams } from "react-router-dom";
import { SnackbarContext } from "../contexts/snackbarContext";
import { ProjectMeta, Status } from "../graphql/gqlTypes";
import { POLESETS } from "../graphql/gqlPoleset";

const EditProjectModal = ({
  isOpen,
  close,
  project,
}: {
  isOpen: boolean;
  close: () => void;
  project: ProjectMeta;
}) => {
  const theme = useTheme();
  const { userId } = useParams();
  const { openSuccessSnackbar } = useContext(SnackbarContext);

  const [editName, setEditName] = useState(project.name);
  const [editDescription, setEditDescription] = useState(project.description);
  const [editStatus, setEditStatus] = useState<Status>(project.status);
  const [editPolesetId, setEditPolesetId] = useState(
    project.poleset?.id || "__default__"
  );

  const polesetsQuery = useQuery(POLESETS, {
    variables: { userId },
    skip: !userId,
  });
  const polesets = polesetsQuery?.data?.getPolesetsByUserId || [];

  const refetchBlocks = useMemo(
    () => [
      { query: PROJECTS, variables: { userId } },
      {
        query: GET_PROJECTS_BY_STATUS,
        variables: {
          status: "PUBLIC",
          pagination: { cursor: null, limit: 12 },
        },
      },
      {
        query: GET_PROJECTS_BY_STATUS,
        variables: {
          status: "SHARED",
          pagination: { cursor: null, limit: 12 },
        },
      },
      {
        query: GET_PROJECTS_BY_STATUS,
        variables: {
          status: "PRIVATE",
          pagination: { cursor: null, limit: 12 },
        },
      },
    ],
    [userId]
  );

  const [editProject, { loading: saving }] = useMutation(EDIT_PROJECT, {
    awaitRefetchQueries: true,
    onCompleted: () => {
      openSuccessSnackbar(<T str="Project successfully edited" />);
    },
    onError: (error) => console.error(error),
  });

  const statusLabel = (s: Status) =>
    s === "PRIVATE" ? (
      <T str="Private" />
    ) : s === "SHARED" ? (
      <T str="Shared" />
    ) : (
      <T str="Public" />
    );

  const handleEditSubmit = async () => {
    await editProject({
      variables: {
        projectId: project.id,
        newName: editName,
        newDescription: editDescription,
        newStatus: editStatus,
        newPolesetId: editPolesetId === "__default__" ? null : editPolesetId,
      },
      refetchQueries: refetchBlocks,
    });

    close();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <DialogTitle>
        <T str="Edit project" />
      </DialogTitle>
      <DialogContent>
        <TextField
          label={<T str="Project name" />}
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          fullWidth
          color="secondary"
          sx={{ mt: 2 }}
        />
        <TextField
          label={<T str="Description" />}
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          fullWidth
          multiline
          color="secondary"
          rows={3}
          sx={{ mt: 2 }}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel color="secondary">
            <T str="Poleset" />
          </InputLabel>
          <Select
            value={editPolesetId}
            label="Poleset"
            onChange={(e) => setEditPolesetId(e.target.value as string)}
            color="secondary"
          >
            <MenuItem value="__default__">
              <T str="Default poleset" />
            </MenuItem>
            {polesets.map((set: any) => (
              <MenuItem key={set.id} value={set.id}>
                {set.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          <T str="Visibility" />
        </Typography>
        <RadioGroup
          value={editStatus}
          onChange={(e) => setEditStatus(e.target.value as Status)}
        >
          {(["PRIVATE", "SHARED", "PUBLIC"] as Status[]).map((s) => (
            <FormControlLabel
              key={s}
              value={s}
              control={
                <Radio
                  color={editStatus === s ? "secondary" : "default"}
                  sx={{
                    color: (theme) =>
                      editStatus === s
                        ? theme.palette.secondary.main
                        : theme.palette.text.primary,
                    "&.Mui-checked": {
                      color: (theme) => theme.palette.secondary.main,
                    },
                  }}
                />
              }
              label={statusLabel(s)}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          onClick={close}
          disabled={saving}
        >
          <T str="Cancel" />
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleEditSubmit}
          disabled={saving}
        >
          <T str="Save" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectModal;
